import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImage } from "../data/content";

export default function Lightbox({ items, index, onClose, onNavigate }) {
  const open = index !== null && index !== undefined && items[index];

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onNavigate(1);
      else if (e.key === "ArrowLeft") onNavigate(-1);
    };
    window.addEventListener("keydown", onKey);
    // prevent background scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, onNavigate]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="gallery-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-charcoal/95 p-6 backdrop-blur-sm"
          onClick={onClose}
        >
          <button
            data-testid="lightbox-close-btn"
            onClick={onClose}
            className="absolute right-6 top-6 z-10 text-ivory/70 transition-colors hover:text-brass"
            aria-label="Close gallery"
          >
            <X className="h-7 w-7" />
          </button>

          <span className="absolute left-1/2 top-7 -translate-x-1/2 text-[11px] tracking-[0.3em] text-ivory/50">
            {index + 1} / {items.length}
          </span>

          <button
            data-testid="lightbox-prev-btn"
            onClick={(e) => { e.stopPropagation(); onNavigate(-1); }}
            className="absolute left-4 top-1/2 z-10 -translate-y-1/2 text-ivory/60 transition-colors hover:text-brass sm:left-10"
            aria-label="Previous image"
          >
            <ChevronLeft className="h-9 w-9" />
          </button>

          <motion.figure
            key={index}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.5}
            onDragEnd={(e, info) => {
              if (info.offset.x < -80) onNavigate(1);
              else if (info.offset.x > 80) onNavigate(-1);
            }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="max-h-[78vh] max-w-4xl cursor-grab touch-pan-y active:cursor-grabbing"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={resolveImage(items[index].uri || items[index].image, 1600, 1600)}
              alt={items[index].caption}
              draggable={false}
              className="max-h-[70vh] w-full select-none object-contain"
            />
            <figcaption className="mt-4 text-center font-serif text-base italic text-ivory/70">
              {items[index].caption}
            </figcaption>
          </motion.figure>

          <button
            data-testid="lightbox-next-btn"
            onClick={(e) => { e.stopPropagation(); onNavigate(1); }}
            className="absolute right-4 top-1/2 z-10 -translate-y-1/2 text-ivory/60 transition-colors hover:text-brass sm:right-10"
            aria-label="Next image"
          >
            <ChevronRight className="h-9 w-9" />
          </button>

          <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.25em] text-ivory/35 sm:hidden">
            SWIPE TO EXPLORE
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
